import Axios from "axios";

const state = {
  events: [],
  userEvents: [],
  loading: false,
  error: null,
  selectedEvent: null,
};

const getters = {
  allEvents: (state) => state.events,
  userEvents: (state) => state.userEvents,
  isLoading: (state) => state.loading,
  error: (state) => state.error,
  selectedEvent: (state) => state.selectedEvent,
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_EVENTS(state, events) {
    state.events = events;
  },
  SET_USER_EVENTS(state, events) {
    state.userEvents = events;
  },
  SET_SELECTED_EVENT(state, event) {
    state.selectedEvent = event;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  /**
   * Get all Microsoft 365 users
   * @param {*} context 
   * @returns Promise<Array>
   */
  async getUsers({ commit, rootState }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch users");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch users";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Get a specific user by ID or User Principal Name (UPN)
   * @param {*} context 
   * @param {String} userIdOrUpn - User ID or email
   * @returns Promise<Object>
   */
  async getUserById({ commit, rootState }, userIdOrUpn) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch user");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch user";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Search Microsoft 365 users by query
   * @param {*} context 
   * @param {String} query - Search query (name, email, etc.)
   * @returns Promise<Array>
   */
  async searchUsers({ commit, rootState }, query) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/search`,
        {
          params: {
            query: query
          }
        }
      );
      
      if (response.data.status === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to search users");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to search users";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Get calendar events for a specific user
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, includeCheckIn: Boolean }
   * @returns Promise<Array>
   */
  async getUserEvents({ commit, rootState }, payload) {
    const { userIdOrUpn, includeCheckIn = false } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events`,
        {
          params: {
            includeCheckIn: includeCheckIn
          }
        }
      );
      
      console.log('getUserEvents response:', response.data);
      
      // Check for both success formats
      if (response.data.status === 200 || response.data.success) {
        const events = response.data.data || [];
        commit("SET_USER_EVENTS", events);
        return events;
      } else {
        throw new Error(response.data.message || "Failed to fetch events");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch events";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Get events created by/organized by a specific user
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, includeCheckIn: Boolean }
   * @returns Promise<Array>
   */
  async getEventsByCreator({ commit, rootState }, payload) {
    const { userIdOrUpn, includeCheckIn = false } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/created`,
        {
          params: {
            includeCheckIn: includeCheckIn
          }
        }
      );
      
      console.log('getEventsByCreator response:', response.data);
      
      // Check for both success formats
      if (response.data.status === 200 || response.data.success) {
        const events = response.data.data || [];
        commit("SET_EVENTS", events);
        return events;
      } else {
        throw new Error(response.data.message || "Failed to fetch created events");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch created events";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Create a new calendar event
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, eventData: Object }
   * @returns Promise<Object>
   */
  async createEvent({ commit, rootState }, payload) {
    const { userIdOrUpn, eventData } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events`,
        eventData
      );
      
      console.log('createEvent response:', response.data);
      
      // Check for both success formats (status 201 for created, or success: true)
      if (response.data.status === 201 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to create event");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to create event";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Update an existing calendar event
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, eventId: String, eventData: Object }
   * @returns Promise<Object>
   */
  async updateEvent({ commit, rootState }, payload) {
    const { userIdOrUpn, eventId, eventData } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.patch(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}`,
        eventData
      );
      
      console.log('updateEvent response:', response.data);
      
      if (response.data.status === 200 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to update event");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to update event";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Generate check-in QR code for an event
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, eventId: String, attendeeEmail: String }
   * @returns Promise<Object> - { qrCodeUrl: String, checkInUrl: String }
   */
  async generateCheckInQR({ commit, rootState }, payload) {
    const { userIdOrUpn, eventId, attendeeEmail } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/generate-checkin-qr`,
        null,
        {
          params: {
            attendeeEmail: attendeeEmail
          }
        }
      );
      
      console.log('generateCheckInQR response:', response.data);
      
      if (response.data.status === 200 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to generate QR code");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to generate QR code";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Check-in to an event
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, eventId: String, checkInData: Object }
   * @returns Promise<Object>
   */
  async checkInEvent({ commit, rootState }, payload) {
    const { userIdOrUpn, eventId, checkInData } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/checkin`,
        checkInData
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to check-in");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to check-in";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Send an email via Microsoft 365
   * @param {*} context 
   * @param {Object} payload - { userIdOrUpn: String, mailData: Object }
   * @returns Promise<Object>
   */
  async sendMail({ commit, rootState }, payload) {
    const { userIdOrUpn, mailData } = payload;
    
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");
    
    try {
      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/sendMail`,
        mailData
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to send mail");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to send mail";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Clear error state
   * @param {*} context 
   */
  clearError({ commit }) {
    commit("CLEAR_ERROR");
  },

  /**
   * Set selected event
   * @param {*} context 
   * @param {*} event 
   */
  setSelectedEvent({ commit }, event) {
    commit("SET_SELECTED_EVENT", event);
  },

  /**
   * Get event details (public - no auth required for check-in page)
   * @param {*} context
   * @param {Object} payload - { userIdOrUpn, eventId }
   * @returns Promise<Object>
   */
  async getEventDetails({ commit, rootState }, { userIdOrUpn, eventId }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const token = localStorage.getItem("token");
      const config = token ? {
        headers: { Authorization: `Bearer ${token}` }
      } : {};

      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}`,
        config
      );

      // Handle both response formats
      if (response.data.status === 200 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to get event details");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to get event details";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Submit check-in (public - no auth required)
   * @param {*} context
   * @param {Object} checkInData - Check-in form data
   * @returns Promise<Object>
   */
  async submitCheckIn({ commit, rootState }, checkInData) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/events/checkin`,
        checkInData
      );

      // Handle both response formats
      if (response.data.status === 200 || response.data.status === 201 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Check-in failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Check-in failed";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Get event attachments
   * @param {*} context
   * @param {Object} params - { userIdOrUpn, eventId }
   * @returns Promise<Array>
   */
  async getEventAttachments({ commit, rootState }, { userIdOrUpn, eventId }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/attachments`
      );

      if (response.data.status === 200 || response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.message || "Failed to get attachments");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to get attachments";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Upload event attachment
   * @param {*} context
   * @param {Object} params - { userIdOrUpn, eventId, file, fileName }
   * @returns Promise<Object>
   */
  async uploadEventAttachment({ commit, rootState }, { userIdOrUpn, eventId, file, fileName }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', fileName);

      const response = await Axios.post(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/attachments`,
        formData
        // Note: Don't set Content-Type header manually for FormData
        // Axios will automatically set it with the correct boundary
      );

      if (response.data.status === 201 || response.data.status === 200 || response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to upload attachment");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to upload attachment";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Delete event attachment
   * @param {*} context
   * @param {Object} params - { userIdOrUpn, eventId, attachmentId }
   * @returns Promise<void>
   */
  async deleteEventAttachment({ commit, rootState }, { userIdOrUpn, eventId, attachmentId }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await Axios.delete(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/attachments/${attachmentId}`
      );

      if (response.data.status === 200 || response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to delete attachment");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to delete attachment";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Download event attachment
   * @param {*} context
   * @param {Object} params - { userIdOrUpn, eventId, attachmentId, fileName }
   * @returns Promise<void>
   */
  async downloadEventAttachment({ commit, rootState }, { userIdOrUpn, eventId, attachmentId, fileName }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await Axios.get(
        `${rootState.host}/api/integration/microsoft/users/${userIdOrUpn}/events/${eventId}/attachments/${attachmentId}/download`,
        {
          responseType: 'blob' // Important for file downloads
        }
      );

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to download attachment";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
